import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "react-toastify";
import { ArrowRight, Check, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Button } from "~/atoms/button";
import { DocumentConfetti } from "~/molecules/DocumentConfetti";
import { OnboardFooter } from "~/molecules/onboard-documents/OnboardFooter";
import { CompleteTable } from "~/molecules/onboard-documents/CompleteTable";
import { useDocumentReleases } from "~/api/v2/useDocumentReleases";
import { updateDocument, updateDocumentRelease } from "~/api/v2/requests";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { StickyHorizontalScrollbar } from "~/molecules/StickyHorizontalScrollbar";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";
import { DocumentArtifactStatus } from "~/config/types";
import {
  type DocumentReleaseSummary,
  type ScheduleReviewPeriod,
} from "~/api/types";
import { setMapItems } from "~/helpers/map";
import { removeFromSet } from "~/helpers/set";

interface CompleteStepProps {
  createdReleaseIds: number[];
  isCompleted: boolean;
  onComplete: () => void;
  onRestart: () => void;
}

export const CompleteStep = ({
  createdReleaseIds,
  isCompleted,
  onComplete,
  onRestart,
}: CompleteStepProps) => {
  const { t } = useTranslation();
  const { data: allReleases, mutate } = useDocumentReleases();

  const releases = useMemo<DocumentReleaseSummary[]>(() => {
    if (!allReleases) return [];
    const createdSet = new Set(createdReleaseIds);
    return allReleases.filter((release) => createdSet.has(release.id));
  }, [allReleases, createdReleaseIds]);

  const orderedRows = useMemo<DocumentReleaseSummary[]>(
    () =>
      [...releases].sort((a, b) =>
        a.artifact.title.localeCompare(b.artifact.title),
      ),
    [releases],
  );

  const {
    selectedIds,
    allSelected,
    toggleRow,
    toggleAll,
    clearSelection,
    bulkTargets,
  } = useBulkSelection(orderedRows.map((r) => r.id));

  const [statusOverrides, setStatusOverrides] = useState<
    Map<number, DocumentArtifactStatus>
  >(() => new Map());
  const [reviewPeriodOverrides, setReviewPeriodOverrides] = useState<
    Map<number, ScheduleReviewPeriod>
  >(() => new Map());
  const [publishingIds, setPublishingIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [isCompleting, setIsCompleting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const statusById = useMemo(() => {
    const map = new Map<number, DocumentArtifactStatus>();
    for (const row of releases) {
      map.set(
        row.id,
        statusOverrides.get(row.id) ?? DocumentArtifactStatus.published,
      );
    }
    return map;
  }, [releases, statusOverrides]);

  const reviewPeriodById = useMemo(() => {
    const map = new Map<number, ScheduleReviewPeriod>();
    for (const row of releases) {
      map.set(
        row.id,
        reviewPeriodOverrides.has(row.id)
          ? reviewPeriodOverrides.get(row.id)!
          : row.document.scheduleReviewPeriod,
      );
    }
    return map;
  }, [releases, reviewPeriodOverrides]);

  const setStatus = (
    id: number,
    status: DocumentArtifactStatus,
    opts?: { bulk?: boolean },
  ) => {
    const targets = opts?.bulk ? bulkTargets(id) : new Set([id]);
    setStatusOverrides((prev) => setMapItems(prev, targets, status));
  };

  const setReviewPeriod = (
    id: number,
    period: ScheduleReviewPeriod,
    opts?: { bulk?: boolean },
  ) => {
    const targets = opts?.bulk ? bulkTargets(id) : new Set([id]);
    setReviewPeriodOverrides((prev) => setMapItems(prev, targets, period));
  };

  const getReviewPeriodForRow = (row: DocumentReleaseSummary) =>
    reviewPeriodById.get(row.id) ?? null;

  const saveReviewPeriod = async (
    row: DocumentReleaseSummary,
  ): Promise<boolean> => {
    const scheduleReviewPeriod = getReviewPeriodForRow(row);
    if (scheduleReviewPeriod === row.document.scheduleReviewPeriod) {
      return true;
    }

    try {
      await updateDocument(row.document.id, { scheduleReviewPeriod });
      await mutate(
        (current) =>
          current?.map((release) =>
            release.id === row.id
              ? {
                  ...release,
                  document: {
                    ...release.document,
                    scheduleReviewPeriod,
                  },
                }
              : release,
          ),
        { revalidate: false },
      );
      return true;
    } catch {
      return false;
    }
  };

  const publishRow = async (id: number): Promise<boolean> => {
    try {
      await updateDocumentRelease(id, {
        status: DocumentArtifactStatus.published,
      });
      await mutate(
        (current) =>
          current?.map((release) =>
            release.id === id
              ? {
                  ...release,
                  artifact: {
                    ...release.artifact,
                    status: DocumentArtifactStatus.published,
                  },
                }
              : release,
          ),
        { revalidate: false },
      );
      return true;
    } catch {
      return false;
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const reviewPeriodUpdates = orderedRows.filter(
        (row) =>
          getReviewPeriodForRow(row) !== row.document.scheduleReviewPeriod,
      );
      const toPublish = orderedRows.filter(
        (r) =>
          r.artifact.status === DocumentArtifactStatus.draft &&
          r.artifact.permissions.canPublish &&
          statusById.get(r.id) === DocumentArtifactStatus.published,
      );

      let updatedReviewPeriodCount = 0;
      const failedReviewPeriodIds: number[] = [];
      for (const row of reviewPeriodUpdates) {
        if (await saveReviewPeriod(row)) updatedReviewPeriodCount++;
        else failedReviewPeriodIds.push(row.id);
      }

      if (failedReviewPeriodIds.length > 0) {
        void mutate();
        toast.error(
          t(
            ($) =>
              $.library.onboardDocuments.steps.configureAndPublish.toasts
                .failedRecurringReview,
            { count: failedReviewPeriodIds.length },
          ),
        );
        return;
      }

      if (updatedReviewPeriodCount > 0) {
        toast.success(
          t(
            ($) =>
              $.library.onboardDocuments.steps.configureAndPublish.toasts
                .recurringReviewUpdated,
            { count: updatedReviewPeriodCount },
          ),
        );
      }

      if (toPublish.length === 0) {
        setShowConfetti(true);
        onComplete();
        return;
      }

      setPublishingIds(new Set(toPublish.map((r) => r.id)));

      let succeededCount = 0;
      const failedIds: number[] = [];
      for (const row of toPublish) {
        if (await publishRow(row.id)) succeededCount++;
        else failedIds.push(row.id);
        setPublishingIds((prev) => removeFromSet(prev, row.id));
      }

      if (failedIds.length > 0) void mutate();

      if (succeededCount > 0) {
        toast.success(
          t(
            ($) =>
              $.library.onboardDocuments.steps.configureAndPublish.toasts
                .published,
            { count: succeededCount },
          ),
        );
      }
      if (failedIds.length > 0) {
        toast.error(
          t(
            ($) =>
              $.library.onboardDocuments.steps.configureAndPublish.toasts
                .failedPublish,
            { count: failedIds.length },
          ),
        );
        return;
      }

      setShowConfetti(true);
      onComplete();
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish.cardTitle,
            )}
          </CardTitle>
          <CardDescription>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish
                  .cardDescription,
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CompleteTable
            readOnly={isCompleted || isCompleting}
            wrapperRef={tableWrapperRef}
            rows={orderedRows}
            selectedIds={selectedIds}
            allSelected={allSelected}
            publishingIds={publishingIds}
            statusById={statusById}
            reviewPeriodById={reviewPeriodById}
            onToggleAll={toggleAll}
            onToggleRow={toggleRow}
            onUnselectAll={clearSelection}
            onStatusChange={setStatus}
            onReviewPeriodChange={setReviewPeriod}
          />
        </CardContent>
      </Card>
      <StickyHorizontalScrollbar
        targetRef={tableWrapperRef}
        deps={[orderedRows, isCompleted]}
        className="sticky bottom-12 h-4"
      />
      <OnboardFooter
        right={
          isCompleted ? (
            <>
              <Button variant="outline" onClick={onRestart}>
                <RefreshCw className="size-4" />
                {t(
                  ($) =>
                    $.library.onboardDocuments.steps.configureAndPublish
                      .onboardMore,
                )}
              </Button>
              <Button asChild>
                <Link to="/documents?status=published&status=draft">
                  {t(
                    ($) =>
                      $.library.onboardDocuments.steps.configureAndPublish
                        .goToLibrary,
                  )}
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </>
          ) : (
            <Button onClick={handleComplete} disabled={isCompleting}>
              <Check />
              {t(
                ($) =>
                  $.library.onboardDocuments.steps.configureAndPublish
                    .completeOnboarding,
              )}
            </Button>
          )
        }
      />
      <UnsavedChangesAlert
        hasUnsavedChanges={!isCompleted}
        title={t(($) => $.library.onboardDocuments.leaveAlert.title)}
        description={t(
          ($) => $.library.onboardDocuments.leaveAlert.publishDescription,
        )}
        cancelLabel={t(($) => $.library.onboardDocuments.leaveAlert.stay)}
        confirmLabel={t(($) => $.library.onboardDocuments.leaveAlert.leave)}
      />
      <DocumentConfetti
        show={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />
    </div>
  );
};
